/**
 * Compact strip rendered between the hero map and the player bar when the
 * round is in Stableford or Match-play mode. Shows the live total points or
 * match status banner. Returns null for strokeplay or when the scoring API
 * hasn't replied yet (offline).
 */
export default function ScoringStrip({ round, scoring, currentHole, activePlayer }) {
  const mode = round?.scoring_mode;
  if (!mode || mode === "strokeplay" || !scoring) return null;

  if (mode === "stableford") {
    const perPlayer = scoring.per_player || [];
    const me = perPlayer.find((p) => p.player_index === activePlayer) || perPlayer[0];
    if (!me) return null;
    // Strokes to bank 2 pts on this hole = par + strokes_received_on_this_hole.
    // Use the snapshot we get back from the most recent per_hole entry where
    // applied. For the current hole we look it up from the player's per_hole
    // breakdown — if not played yet, compute par + (applied_handicap-based
    // estimate). For simplicity, default to par when no SI data.
    const par = currentHole?.par ?? null;
    let strokesForPar = par;
    if (scoring.net && currentHole) {
      // Prefer the WHS Course Handicap returned by the scoring API (uses
      // course rating + slope). Fall back to the applied handicap index
      // rounded when the backend didn't compute a CH (missing rating/slope).
      const hcp = me.course_handicap != null
        ? me.course_handicap
        : (me.applied_handicap != null ? Math.round(me.applied_handicap) : null);
      if (hcp != null && hcp > 0) {
        const si = currentHole.stroke_index;
        const base = Math.floor(hcp / 18);
        const extra = si != null && si >= 1 && si <= (hcp % 18) ? 1 : 0;
        strokesForPar = (par ?? 0) + base + extra;
      }
    }
    return (
      <div
        data-testid="scoring-strip-stableford"
        className="mx-0 my-3 rounded-2xl px-4 py-3 flex items-center justify-between gap-3"
        style={{ background: "var(--accent-sand)", border: "1.5px solid var(--border)" }}
      >
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[3px] font-bold text-[var(--text-secondary)] leading-none mb-0.5">
            Stableford {scoring.net ? "· Net" : "· Gross"}
            {scoring.net && me.course_handicap != null && (
              <span
                data-testid="scoring-strip-course-handicap"
                className="ml-1.5 px-1.5 py-0.5 rounded bg-white text-[var(--primary)] tracking-normal normal-case"
                title={`Course Handicap: ${me.course_handicap} (from Index ${me.applied_handicap})`}
              >
                CH {me.course_handicap}
              </span>
            )}
          </div>
          <div className="font-heading font-black tracking-tighter-2 text-[var(--primary)] text-2xl leading-none">
            {me.points} <span className="text-sm font-bold opacity-70 ml-0.5">pts</span>
          </div>
        </div>
        {par != null && (
          <div
            className="text-right rounded-xl px-3 py-1.5"
            style={{ background: "white", border: "1.5px solid var(--border)" }}
          >
            <div className="text-[9px] uppercase tracking-wider font-bold text-[var(--text-secondary)] leading-none">
              For 2 pts
            </div>
            <div className="font-mono-num font-black text-[var(--primary)] text-lg leading-tight">
              ≤{strokesForPar} <span className="text-[10px] font-bold opacity-70">on this hole</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (mode === "match_play") {
    const status = scoring.status || {};
    const teamAName = (scoring.team_a_names || []).join(" & ") || "Team A";
    const teamBName = (scoring.team_b_names || []).join(" & ") || "Team B";
    const winning = (status.diff || 0) > 0;
    const losing = (status.diff || 0) < 0;
    const bg = winning ? "var(--primary)" : losing ? "var(--accent-terracotta)" : "var(--text)";
    return (
      <div
        data-testid="scoring-strip-match"
        className="mx-0 my-3 rounded-2xl px-4 py-3"
        style={{ background: bg, color: "#F2EEE7" }}
      >
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[3px] font-bold opacity-80 leading-none mb-0.5">
              Match play
            </div>
            <div className="font-heading font-black tracking-tighter-2 text-2xl leading-none" data-testid="match-status-label">
              {status.label || "—"}
            </div>
          </div>
          <div className="text-right text-[11px] uppercase tracking-wider font-bold opacity-90 leading-tight">
            <div className="truncate max-w-[8rem]">{teamAName}</div>
            <div className="opacity-60 text-[9px] my-0.5">vs</div>
            <div className="truncate max-w-[8rem]">{teamBName}</div>
          </div>
        </div>
        {status.decisive && status.complete && (
          <div className="mt-2 text-[10px] uppercase tracking-wider font-bold opacity-90">
            Match decided — closeout!
          </div>
        )}
      </div>
    );
  }

  return null;
}
